/**
 * Pure helpers over a project's presence snapshot (utils/presenceClient.js): who is where, as the header, the file
 * tree, the kanban and the editor show it. A snapshot is `{ you, people, agents }` as vfs-service sends it
 * (src/collab/presence.js): `people[]` `{ id, sub, name, initials, slot, role, state, views: [{ kind, path?, service?,
 * session?, cursor?, typing, at }] }`, `agents[]` `{ id, label, client, driver: { name, initials, slot }, slot, paths,
 * lines: { [path]: [[from, to]] }, model, tool, step, steps, sessionId }` (phase 4: the lines its last write touched in
 * each file, the model and tool behind it, and, for Flow's chat, the step of its run and the chat it runs in).
 * Paths are vfs paths (`services/<name>/...`).
 */

/** Everyone but me. */
export function othersOf(snapshot) {
  const you = snapshot?.you;
  return (snapshot?.people || []).filter((person) => !you || person.id !== you.id);
}

const baseName = (path) => String(path || '').split('/').pop();

/** "Flow chat for Luca", "Claude Code for you". */
export function agentName(agent, you) {
  const driver = agent?.driver;
  if (!driver) { return agent?.label || 'AI agent'; }
  const mine = you && driver.id === you.id;
  return `${agent.label} for ${mine ? 'you' : firstName(driver.name)}`;
}

export const firstName = (name) => String(name || '').split(/\s+/)[0] || 'Someone';

/** What a person is doing, in a few words: "typing in App.jsx", "viewing App.jsx", "on Home", "in a chat". */
export function activityOf(person) {
  const views = person?.views || [];
  const typing = views.find((view) => view.typing && view.path);
  if (typing) { return `typing in ${baseName(typing.path)}`; }
  const file = [...views].sort((a, b) => (b.at || 0) - (a.at || 0)).find((view) => view.kind === 'file' && view.path);
  if (file) { return `viewing ${baseName(file.path)}`; }
  const latest = [...views].sort((a, b) => (b.at || 0) - (a.at || 0))[0];
  if (!latest) { return 'here'; }
  if (latest.kind === 'chat') { return 'in a chat'; }
  if (latest.kind === 'service') { return latest.service ? `on ${latest.service}` : 'on a service'; }
  if (latest.kind === 'preview') { return 'on the preview'; }
  // Someone who shows only while typing, reading quietly (phase 4).
  if (latest.kind === 'here') { return 'here'; }
  return 'on Home';
}

/**
 * Who is on one file (`path`) or, for a folder, anywhere inside it (`folder: true`): people with their verb
 * ("editing" while typing, else "viewing"), and the agents writing there.
 */
export function presenceAt(snapshot, path, { folder = false } = {}) {
  if (!path) { return { people: [], agents: [] }; }
  const inside = (candidate) => Boolean(candidate) && (folder ? candidate.startsWith(`${path}/`) : candidate === path);
  const people = [];
  othersOf(snapshot).forEach((person) => {
    const views = (person.views || []).filter((view) => inside(view.path));
    if (views.length === 0) { return; }
    people.push({ person, verb: views.some((view) => view.typing) ? 'editing' : 'viewing' });
  });
  const agents = (snapshot?.agents || []).filter((agent) => (agent.paths || []).some(inside));
  return { people, agents };
}

/** Other people's cursors in one file: `{ person, cursor }` (only those who share their cursor). */
export function cursorsIn(snapshot, path) {
  const out = [];
  othersOf(snapshot).forEach((person) => {
    (person.views || []).forEach((view) => {
      if (view.cursor && view.cursor.path === path) { out.push({ person, cursor: view.cursor }); }
    });
  });
  return out;
}

const NEAR_LINES = 8;

/**
 * The soft warning for typing at `line` of `path`, or null: an AI agent writing this file (it may replace what you
 * type), or someone else typing a few lines away. Never a lock: the editor stays editable.
 */
export function softWarningFor(snapshot, path, line) {
  if (!path) { return null; }
  const agent = (snapshot?.agents || []).find((candidate) => (candidate.paths || []).includes(path));
  if (agent) {
    return {
      kind: 'agent',
      agent,
      message: `${agentName(agent, snapshot?.you)} is writing this file. Its next write may replace what you type here.`,
    };
  }
  if (!Number.isInteger(line)) { return null; }
  const near = cursorsIn(snapshot, path).find(({ cursor }) => cursor.typing && Math.abs(cursor.line - line) <= NEAR_LINES);
  if (near) {
    return {
      kind: 'person',
      person: near.person,
      message: `${firstName(near.person.name)} is editing near line ${near.cursor.line}. You can type too; they see your cursor.`,
    };
  }
  return null;
}

/**
 * For a chat session card on the kanban: the live strip ("Editing now") of its project. The agents writing for the
 * card's person (`driverId`, null for anyone) and the other people editing in the project, agents first, at most
 * `max`, and how many more.
 */
export function editingNow(snapshot, { driverId = null, max = 2 } = {}) {
  const agents = (snapshot?.agents || []).filter((agent) => (agent.paths || []).length > 0 && (!driverId || agent.driver?.id === driverId));
  const people = othersOf(snapshot).filter((person) => (person.views || []).some((view) => view.typing && view.path));
  const entries = [
    ...agents.map((agent) => ({ kind: 'agent', agent, text: `${agentName(agent, snapshot?.you)} writing ${(agent.paths || []).map(baseName).slice(0, 2).join(', ')}` })),
    ...people.map((person) => ({ kind: 'person', person, text: `${firstName(person.name)} ${activityOf(person)}` })),
  ];
  return { shown: entries.slice(0, max), more: Math.max(0, entries.length - max) };
}

/** May `you` pause (or resume) this agent: the project's owner any agent, an editor only the agents they drive. */
export function canPause(agent, snapshot) {
  const you = snapshot?.you;
  if (!agent || !you) { return false; }
  if (you.role === 'owner') { return true; }
  return you.role === 'editor' && Boolean(agent.driver?.id) && agent.driver.id === you.id;
}

/** "paused by Marta" / "paused by you", or null. */
export function pausedLabel(agent, you) {
  const by = agent?.paused?.by;
  if (!agent?.paused) { return null; }
  if (!by) { return 'paused'; }
  return `paused by ${you && by.id === you.id ? 'you' : firstName(by.name)}`;
}

/** The person you follow (a presence entry), or null when you follow nobody or they left. */
export function followedPerson(snapshot) {
  const id = snapshot?.following;
  if (!id) { return null; }
  return othersOf(snapshot).find((person) => person.id === id) || null;
}

/**
 * Where a followed person is: their most recent file view `{ path, cursor }` (the latest one they moved in), or null
 * when they have no file open.
 */
export function followTarget(person) {
  const files = (person?.views || []).filter((view) => view.kind === 'file' && view.path);
  if (files.length === 0) { return null; }
  const latest = [...files].sort((a, b) => (b.at || 0) - (a.at || 0))[0];
  return { path: latest.path, cursor: latest.cursor || null };
}

/** The people following you right now. */
export function followersOf(snapshot) {
  const you = snapshot?.you;
  if (!you) { return []; }
  return othersOf(snapshot).filter((person) => person.following && person.following === you.id);
}

/** A vfs path split for the editor: `services/web/src/App.jsx` → `{ service: 'services/web', file: 'src/App.jsx' }`. */
export function splitVfsPath(path) {
  const parts = String(path || '').split('/');
  if (parts[0] === 'services' && parts.length > 2) { return { service: `services/${parts[1]}`, file: parts.slice(2).join('/') }; }
  return { service: null, file: String(path || '') };
}

// ---- Phase 4: what an agent is doing (vfs-service presence: lines, model, tool, step, steps, sessionId) ----

/** An agent's line ranges in one file (`[[18, 21]]`), from presence `agent.lines`; [] when none are known. */
export function agentLinesIn(agent, path) {
  const ranges = agent?.lines?.[path];
  if (!Array.isArray(ranges)) { return []; }
  return ranges.filter((r) => Array.isArray(r) && Number.isInteger(r[0]) && Number.isInteger(r[1]) && r[0] >= 1 && r[1] >= r[0]);
}

/** "line 7", "lines 18–21", "lines 3–4 and 18–21", "lines 3–4, 9 and 2 more places". */
export function linesLabel(ranges) {
  if (!ranges?.length) { return ''; }
  const part = ([a, b]) => (a === b ? `${a}` : `${a}–${b}`);
  if (ranges.length === 1) { return ranges[0][0] === ranges[0][1] ? `line ${ranges[0][0]}` : `lines ${part(ranges[0])}`; }
  if (ranges.length === 2) { return `lines ${part(ranges[0])} and ${part(ranges[1])}`; }
  return `lines ${part(ranges[0])}, ${part(ranges[1])} and ${ranges.length - 2} more place${ranges.length - 2 === 1 ? '' : 's'}`;
}

/** "step 4 of 7", "step 4", or null when the agent did not say. */
export function agentStepLabel(agent) {
  const { step, steps } = agent || {};
  if (Number.isInteger(step) && Number.isInteger(steps) && steps > 0) { return `step ${step} of ${steps}`; }
  if (Number.isInteger(step)) { return `step ${step}`; }
  return null;
}

/** A model id as people read it: "gemini-2.5-pro" → "Gemini 2.5 Pro", "claude-sonnet-4" → "Claude Sonnet 4". */
export function modelLabel(model) {
  if (!model) { return null; }
  const name = String(model).split('/').pop();
  return name.split(/[-_\s]+/).filter(Boolean).map((word) => (/^\d/.test(word) ? word : word.charAt(0).toUpperCase() + word.slice(1))).join(' ');
}

/** The band's label in the editor: "Flow chat · Luca", "Claude Code · you". */
export function agentBandLabel(agent, you) {
  const driver = agent?.driver;
  if (!driver) { return agent?.label || 'AI agent'; }
  return `${agent.label} · ${you && driver.id === you.id ? 'you' : firstName(driver.name)}`;
}

/** The agents writing lines of `path` right now: `[{ agent, ranges }]` (only those that said which lines). */
export function agentBandsIn(snapshot, path) {
  if (!path) { return []; }
  return (snapshot?.agents || [])
    .filter((agent) => !agent.paused && (agent.paths || []).includes(path))
    .map((agent) => ({ agent, ranges: agentLinesIn(agent, path) }))
    .filter((band) => band.ranges.length > 0);
}

/** Is `line` inside one of the ranges (with `margin` lines around)? */
export function lineInRanges(ranges, line, margin = 0) {
  if (!Number.isInteger(line)) { return false; }
  return (ranges || []).some(([a, b]) => line >= a - margin && line <= b + margin);
}

// ---- Phase 4: modified since you last saw (vfs-service GET /collab/since-last-seen) ----

/** "LB", "AI", "LB +1": who changed a file since you left, for the tree's "M · LB". */
export function modifiedBy(entry) {
  const by = entry?.by || [];
  if (!by.length) { return ''; }
  const first = by[0].initials || '?';
  return by.length > 1 ? `${first} +${by.length - 1}` : first;
}

/** "Flow chat for Luca", "Luca", "Claude Code for you": one author of a since-you-left entry. */
export function sinceAuthorName(who, you) {
  const id = who?.id || who?.author?.id;
  const name = you && id === `user:${you.id}` ? 'you' : firstName(who?.name || who?.author?.name);
  const via = who?.via;
  if (!via) { return name === 'you' ? 'You' : name; }
  return `${via === 'Flow' ? 'Flow chat' : via} for ${name}`;
}

/** "Changed since you last left: Luca (2), Flow chat for Luca". */
export function modifiedTitle(entry, you) {
  const names = (entry?.by || []).map((who) => sinceAuthorName(who, you));
  return `Changed since you last left${entry?.count ? ` (${entry.count} change${entry.count === 1 ? '' : 's'})` : ''}: ${names.join(', ')}`;
}

/** "23 changes in 9 files: Luca 11, Flow chat for Luca 9, Sara 3". */
export function sinceSummary(data, you, { maxAuthors = 3 } = {}) {
  const changes = data?.changes || 0;
  const files = (data?.files || []).length;
  if (!changes) { return ''; }
  const authors = (data.authors || []).slice(0, maxAuthors).map((a) => `${sinceAuthorName(a, you)} ${a.count}`);
  const more = (data.authors || []).length - authors.length;
  return `${changes} change${changes === 1 ? '' : 's'} in ${files} file${files === 1 ? '' : 's'}: ${authors.join(', ')}${more > 0 ? ` and ${more} more` : ''}`;
}
